import { useEffect, useState } from "react";
import { tdApi } from "@src/services/api/tdApi";
import { dataActions } from "@src/services/state/dataReducer";
import { useDispatch } from "react-redux";

import { useDataState } from "./data";

/**
 * Preload hook for essential app data
 *
 * Loads exchanges and strategies at app startup.
 * These are small datasets needed throughout the app.
 * All other entities (backtests, executions, optimizations, jobs) load per-page.
 */

/**
 * Main preload hook - should only be called ONCE in App.tsx
 * Loads essential data from API at startup
 */
export const usePreloadData = () => {
  const dispatch = useDispatch();
  const [error, setError] = useState<Error | undefined>(undefined);

  useEffect(() => {
    const loadData = async () => {
      try {
        dispatch(dataActions.setIsPreloading(true));

        console.log("[PRELOAD] Loading exchanges, strategies...");

        // Fetch essential entities in parallel
        const [exchangesRes, strategiesRes] = await Promise.all([
          tdApi.getExchanges({ limit: 100 }),
          tdApi.getStrategies({ limit: 100 }),
        ]);

        // Dispatch to Redux
        if (exchangesRes?.items) {
          dispatch(dataActions.setExchanges(exchangesRes.items));
        }
        if (strategiesRes?.items) {
          dispatch(dataActions.setStrategies(strategiesRes.items));
        }

        console.log("[PRELOAD] Loaded:", {
          exchanges: exchangesRes?.items?.length ?? 0,
          strategies: strategiesRes?.items?.length ?? 0,
        });

        dispatch(dataActions.setIsPreloading(false));
        setError(undefined);
      } catch (err) {
        console.error("[PRELOAD] Error loading initial data:", err);
        dispatch(dataActions.setIsPreloading(false));
        setError(err as Error);
      }
    };

    loadData();
  }, [dispatch]);

  return { error };
};

/**
 * Get preloading status from Redux (read-only)
 * Use this in child components instead of calling usePreloadData()
 */
export const useIsPreloading = (): boolean => {
  const dataState = useDataState();
  return dataState.isPreloading;
};
